import { IntlShape } from "react-intl";
import { IssueFormatter } from "../../../application/hooks/useIssueFormatter";
import { ExtraChangeImpact, ExtraImpactBlock } from "../../../domain/entities/ExtraChangeImpact";
import { IssueCode, ValidationIssue } from "../../../domain/entities/ValidationIssue";
import { extraImpactDialogMessages as messages } from "./ExtraImpactDialog.messages";

/** What the page knows about the change, beyond the check result. */
export interface ExtraChangeContext {
  /** Name of the extra in the user locale; used when an issue does not carry `params.extra`. */
  readonly extraName: string;
  /**
   * Set when the type changes to an unsupported one on an extra with a category: the dialog then
   * says that the category fields will be removed.
   */
  readonly typeChange?: { readonly typeName: string };
}

/** Accommodations of one section, flat or grouped by property for AGR. */
export type ImpactPlaceList =
  | { readonly grouped: false; readonly items: readonly string[] }
  | {
      readonly grouped: true;
      readonly groups: readonly { readonly property: string; readonly items: readonly string[] }[];
    };

export interface ImpactSectionView {
  readonly key: string;
  readonly text: string;
  /** The empty-block warning of a deactivation. */
  readonly highlighted: boolean;
  readonly places: ImpactPlaceList;
}

export interface ExtraImpactDialogView {
  readonly title: string;
  readonly preface?: string;
  readonly sections: readonly ImpactSectionView[];
}

export interface ExtraChangeBlockedView {
  readonly sections: readonly ImpactSectionView[];
}

interface ImpactPlace {
  /** `undefined` means the current property. */
  readonly propertyTicker?: string;
  readonly propertyName: string;
  readonly label: string;
}

/** Order of the sections of the combined dialog: stops counting, coverage lost, deactivation. */
const SECTION_ORDER: readonly string[] = [
  IssueCode.EXTRA_CHANGE_STOPS_COUNTING,
  IssueCode.EXTRA_COVERAGE_LOST,
  IssueCode.EXTRA_DEACTIVATION_REMOVES,
  IssueCode.EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY,
];

const DEACTIVATION_CODES: readonly string[] = [
  IssueCode.EXTRA_DEACTIVATION_REMOVES,
  IssueCode.EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY,
];

const sectionRank = (code: string) => {
  const rank = SECTION_ORDER.indexOf(code);
  return rank === -1 ? SECTION_ORDER.length : rank;
};

/**
 * Groups by property name when the places span more than one property or a property other than
 * the current one; otherwise lists the accommodations alone.
 */
export function placeList(
  places: readonly ImpactPlace[],
  currentPropertyTicker: string
): ImpactPlaceList {
  const tickerOf = (place: ImpactPlace) => place.propertyTicker ?? currentPropertyTicker;
  const tickers = new Set(places.map(tickerOf));
  const grouped = tickers.size > 1 || (tickers.size === 1 && !tickers.has(currentPropertyTicker));
  if (!grouped) {
    return { grouped: false, items: places.map(place => place.label) };
  }
  const groups = new Map<string, { property: string; items: string[] }>();
  places.forEach(place => {
    const ticker = tickerOf(place);
    const group = groups.get(ticker) ?? { property: place.propertyName || ticker, items: [] };
    group.items.push(place.label);
    groups.set(ticker, group);
  });
  return { grouped: true, groups: Array.from(groups.values()) };
}

const blockPlace = (block: ExtraImpactBlock, label: string): ImpactPlace => ({
  propertyTicker: block.propertyTicker || undefined,
  propertyName: block.propertyName,
  label,
});

export class ExtraImpactDialogViewModel {
  constructor(
    private readonly intl: IntlShape,
    private readonly formatter: IssueFormatter,
    private readonly currentPropertyTicker: string
  ) {}

  /** One dialog with a section per impact code, never one dialog per impact. */
  confirmation(
    impacts: readonly ExtraChangeImpact[],
    context: ExtraChangeContext
  ): ExtraImpactDialogView {
    const sorted = [...impacts].sort((a, b) => sectionRank(a.code) - sectionRank(b.code));
    const codes = new Set(sorted.map(impact => impact.code));
    const stopsCounting = codes.has(IssueCode.EXTRA_CHANGE_STOPS_COUNTING);
    const extra = this.extraName(sorted, context);
    const preface =
      stopsCounting && context.typeChange
        ? this.intl.formatMessage(messages.typeChangePreface, {
            type: context.typeChange.typeName,
            extra,
          })
        : undefined;
    return {
      title: this.title(codes, extra, context),
      preface,
      sections: sorted.map((impact, index) => this.section(impact, context, index)),
    };
  }

  /** The change-blocked dialog from a check result: only the blocking impacts. */
  blockedByImpacts(
    impacts: readonly ExtraChangeImpact[],
    context: ExtraChangeContext
  ): ExtraChangeBlockedView {
    return { sections: impacts.map((impact, index) => this.section(impact, context, index)) };
  }

  /**
   * The change-blocked dialog from a `FAILED_PRECONDITION` rejection: names come from the issue
   * params, or from the target tickers when there are none. Issues with the same text share a
   * section.
   */
  blockedByIssues(
    issues: readonly ValidationIssue[],
    context: ExtraChangeContext
  ): ExtraChangeBlockedView {
    const sections = new Map<string, ImpactPlace[]>();
    issues
      .filter(issue => issue.is(IssueCode.EXTRA_CHANGE_LEAVES_BLOCK_EMPTY))
      .forEach(issue => {
        const text = this.issueText(issue, context);
        const places = sections.get(text) ?? [];
        const label = issue.param("accommodation") || issue.target.accommodationId;
        if (label) {
          places.push({
            propertyTicker: issue.target.propertyTicker,
            propertyName: issue.param("property") || issue.target.propertyTicker || "",
            label,
          });
        }
        sections.set(text, places);
      });
    return {
      sections: Array.from(sections.entries()).map(([text, places], index) => ({
        key: `${IssueCode.EXTRA_CHANGE_LEAVES_BLOCK_EMPTY}-${index}`,
        text,
        highlighted: false,
        places: placeList(places, this.currentPropertyTicker),
      })),
    };
  }

  private section(
    impact: ExtraChangeImpact,
    context: ExtraChangeContext,
    index: number
  ): ImpactSectionView {
    const emptyBlock = impact.is(IssueCode.EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY);
    const places = impact.blocks.map(block =>
      blockPlace(
        block,
        emptyBlock
          ? this.intl.formatMessage(messages.emptyBlockPlace, {
              accommodation: block.displayName(),
              category: this.formatter.categoryName(block.category),
            })
          : block.displayName()
      )
    );
    return {
      key: `${impact.code}-${index}`,
      text: this.issueText(impact.issue, context),
      highlighted: emptyBlock,
      places: placeList(places, this.currentPropertyTicker),
    };
  }

  private title(codes: Set<string>, extra: string, context: ExtraChangeContext): string {
    const only = (...expected: string[]) =>
      codes.size > 0 && Array.from(codes).every(code => expected.includes(code));
    if (only(...DEACTIVATION_CODES)) {
      return this.intl.formatMessage(messages.titleDeactivation, { extra });
    }
    if (only(IssueCode.EXTRA_CHANGE_STOPS_COUNTING)) {
      return context.typeChange
        ? this.intl.formatMessage(messages.titleCategoryRemoved)
        : this.intl.formatMessage(messages.titleStopsCounting, { extra });
    }
    if (only(IssueCode.EXTRA_COVERAGE_LOST)) {
      return this.intl.formatMessage(messages.titleCoverageLost, { extra });
    }
    return this.intl.formatMessage(messages.titleCombined, { extra });
  }

  private issueText(issue: ValidationIssue, context: ExtraChangeContext): string {
    return this.formatter.format(issue.withParamDefaults({ extra: context.extraName }));
  }

  private extraName(impacts: readonly ExtraChangeImpact[], context: ExtraChangeContext): string {
    const fromIssue = impacts.map(impact => impact.issue.param("extra")).find(Boolean);
    return fromIssue || context.extraName;
  }
}

/** Name of an extra for the dialogs: the user locale first, then any name, then the ticker. */
export function extraDisplayName(
  translations: readonly { readonly locale: string; readonly name: string }[],
  locale: string,
  fallback: string
): string {
  const inLocale = translations.find(translation => translation.locale === locale)?.name;
  return inLocale || translations.map(translation => translation.name).find(Boolean) || fallback;
}
